/**
 * Feed live-events service (LR3).
 *
 * Bridges the realtime spine (LR1 services/socket.ts) to the feed UI:
 *
 *  1. NEW POSTS — when a followed author posts, the server emits `feed:post` to
 *     this user's personal room (`user:<id>`). We fan that out so home.tsx can
 *     prepend the new post live (no full refetch / re-render). The composer also
 *     pushes the just-created post through the SAME channel so the author sees
 *     their own post at the top instantly.
 *
 *  2. LIKE / REPOST COUNTS — when the post AUTHOR is viewing their feed/detail and
 *     someone likes/reposts their post, the server emits `post:like` / `post:repost`
 *     to the author's room with the new authoritative count. We reconcile that into
 *     the shared postEngagement store (deduped by a stable echo id so the socket
 *     echo of the user's OWN action never double-counts).
 *
 * All subscriptions go through LR1's onUserEvent / onReconnect — we do NOT touch
 * services/socket.ts internals. Idempotent: start() wires the bridge once.
 *
 * Defensive by design: never throws; if the socket is offline the REST feed path
 * keeps working and a reconnect/refetch fills any gap.
 */
import { onUserEvent } from './socket';
import { reconcileLikeCount, reconcileRepostCount } from './postEngagement';

// Server event names (emitted from apps/api/src/routes/posts.ts — see the LR3
// API note). All land in the consumer's `user:<id>` personal room.
const FEED_POST_EVENT = 'feed:post';   // { post } — a followed author's new post
const POST_LIKE_EVENT = 'post:like';   // { postId, likeCount, actorId, liked }
const POST_REPOST_EVENT = 'post:repost'; // { postId, repostCount, actorId, reposted }

// --- New-post channel (live prepend) ---
type NewPostListener = (rawPost: any) => void;
const newPostListeners = new Set<NewPostListener>();

// Dedupe ring so the same post id isn't prepended twice (e.g. the author's own
// composer push + the server `feed:post` echo, or a replayed event).
const recentPrependedIds: string[] = [];
function prependAlreadySeen(id: string): boolean {
  if (!id) return false;
  if (recentPrependedIds.includes(id)) return true;
  recentPrependedIds.push(id);
  if (recentPrependedIds.length > 100) recentPrependedIds.shift();
  return false;
}

function fanOutNewPost(rawPost: any) {
  const id = rawPost?.id;
  if (!id || prependAlreadySeen(String(id))) return;
  newPostListeners.forEach((cb) => {
    try {
      cb(rawPost);
    } catch {
      // ignore
    }
  });
}

/**
 * Subscribe to live new posts for the feed. Returns an unsubscribe fn. The
 * payload is the RAW server post shape — the caller normalizes it.
 */
export function onNewFeedPost(cb: NewPostListener): () => void {
  newPostListeners.add(cb);
  return () => {
    newPostListeners.delete(cb);
  };
}

/**
 * Push a locally-created post (from the composer) into the feed live. Goes
 * through the same dedupe so the subsequent `feed:post` server echo (if the
 * author also follows their own posts) won't duplicate it.
 */
export function pushLocalPost(rawPost: any): void {
  fanOutNewPost(rawPost);
}

// --- Wire the socket bridge once ---
let started = false;
const unsubs: Array<() => void> = [];

export function startFeedEvents(): void {
  if (started) return;
  started = true;

  // New post from a followed author → prepend live.
  unsubs.push(
    onUserEvent(FEED_POST_EVENT, (payload: any) => {
      const post = payload?.post ?? payload;
      if (post?.id) fanOutNewPost(post);
    })
  );

  // Live like count for the author's own posts.
  unsubs.push(
    onUserEvent(POST_LIKE_EVENT, (payload: any) => {
      const postId = String(payload?.postId ?? '');
      const count = Number(payload?.likeCount ?? payload?.likes ?? NaN);
      if (!postId || !Number.isFinite(count)) return;
      // Stable echo id: a like + an immediate unlike by the same actor produce
      // different ids (the `liked` flag differs), so both reconcile; a replay of
      // the SAME emit is dropped.
      const echoId = `like:${postId}:${payload?.actorId ?? ''}:${payload?.liked ? 1 : 0}:${count}`;
      reconcileLikeCount(postId, count, echoId);
    })
  );

  // Live repost count for the author's own posts.
  unsubs.push(
    onUserEvent(POST_REPOST_EVENT, (payload: any) => {
      const postId = String(payload?.postId ?? '');
      const count = Number(payload?.repostCount ?? payload?.reposts ?? NaN);
      if (!postId || !Number.isFinite(count)) return;
      const echoId = `repost:${postId}:${payload?.actorId ?? ''}:${payload?.reposted ? 1 : 0}:${count}`;
      reconcileRepostCount(postId, count, echoId);
    })
  );
}

/** Tear down the bridge (e.g. on logout). Never throws. */
export function stopFeedEvents(): void {
  unsubs.forEach((u) => {
    try {
      u();
    } catch {
      // ignore
    }
  });
  unsubs.length = 0;
  newPostListeners.clear();
  recentPrependedIds.length = 0;
  started = false;
}
